/**
 * A seeded PRNG, shared by server and client.
 *
 * Every deer in this game is described by a spawn record that both sides
 * expand into an identical path (see `deer.ts`). The expansion has to produce
 * bit-identical results in two different JavaScript engines, so it uses integer
 * arithmetic only — mulberry32, which is 32-bit integer ops and one float
 * division at the end, rather than anything built on `Math.random` or on
 * floating-point accumulation.
 *
 * This is not a cryptographic PRNG and must never be used for room codes,
 * tokens, or anything an attacker benefits from predicting. `server/ids.ts`
 * owns those, and it uses `node:crypto`.
 */

export interface Rng {
  /** Uniform in [0, 1). */
  next(): number;
  /** Uniform in [lo, hi). */
  range(lo: number, hi: number): number;
  /** Integer in [lo, hi]. */
  int(lo: number, hi: number): number;
  /** True with probability `p`. */
  chance(p: number): boolean;
  /** Uniformly chosen element. Throws on an empty list rather than returning undefined. */
  pick<T>(items: readonly T[]): T;
  /** ±1. */
  sign(): number;
}

export function mulberry32(seed: number): Rng {
  // Force to uint32 up front: a caller passing a float or a negative would
  // otherwise diverge between engines at the first `|0`.
  let a = seed >>> 0;

  const next = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    next,
    range: (lo, hi) => lo + next() * (hi - lo),
    int: (lo, hi) => lo + Math.floor(next() * (hi - lo + 1)),
    chance: (p) => next() < p,
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error('rng.pick on an empty list');
      return items[Math.floor(next() * items.length)]!;
    },
    sign: () => (next() < 0.5 ? -1 : 1),
  };
}

/**
 * A stable 32-bit hash of a string, so a seed can be derived from something
 * legible (`"BRUSH-w3"`) instead of a magic number. FNV-1a.
 */
export function hashSeed(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
